/**
 * Drives the interactive inputs of one quote (KAN-1703).
 *
 * The control answers at once — the new value is shown optimistically, so a
 * rotated model turns on the click — while the reanalysis that re-prices the
 * part runs behind it. Fast clicking must not fire a request per click, nor let
 * an older answer overwrite a newer pick:
 *
 *   * changes are collected for ``debounceMs`` and sent as one PATCH of every
 *     key changed since the last confirmed state;
 *   * at most one request is in flight. A change arriving meanwhile waits and
 *     goes out when the running one settles, so the server stores the picks in
 *     the order the customer made them and the last one wins there too;
 *   * only the answer to the last request lands in the quote cache — an
 *     intermediate one would flash a price the customer has already left.
 *
 * ``refresh`` (KAN-1711) queues a reanalysis with no input change — the
 * configurator saved a pick the analysis reads (``analysis_config_keys``) and
 * the part must be re-measured under it. It goes through the same sender, so a
 * rotation and a configuration change never overtake each other.
 */
import { useQueryClient } from "@tanstack/react-query";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { queryKeys } from "@/api/queries/keys";
import { useReanalyzeQuote } from "@/api/queries/quotes";
import type { Quote } from "@/api/types";

import {
  primaryInteractiveInputs,
  type InteractiveBinding,
} from "./interactiveInputs";

export const REANALYSIS_DEBOUNCE_MS = 400;

export interface InteractiveReanalysis {
  /** ``undefined`` when the part declares no interactive input. */
  binding: InteractiveBinding | undefined;
  /** A change is waiting or being recomputed — the shown price is stale. */
  reanalyzing: boolean;
  /** The last reanalysis failed; the controls fell back to the stored state. */
  failed: boolean;
  /** Re-measure the part under its stored configuration (KAN-1711). */
  refresh: () => Promise<void>;
}

export function useInteractiveReanalysis(
  quote: Quote | undefined,
  debounceMs: number = REANALYSIS_DEBOUNCE_MS,
): InteractiveReanalysis {
  const qc = useQueryClient();
  const slug = quote?.id ?? "";
  const reanalyze = useReanalyzeQuote(slug);
  const mutateAsync = reanalyze.mutateAsync;

  // Picks not yet confirmed by the server, ``key → value`` (``null`` = back
  // to the recommendation). Kept in a ref for the sender and mirrored in state
  // for rendering.
  const pendingRef = useRef<Record<string, unknown>>({});
  const [pending, setPending] = useState<Record<string, unknown>>({});
  const timerRef = useRef<number | null>(null);
  const inFlightRef = useRef(false);
  const dirtyRef = useRef(false);
  // A reanalysis is owed even with no input change (``refresh``).
  const forceRef = useRef(false);
  const [waiting, setWaiting] = useState(false);
  const [inFlight, setInFlight] = useState(false);
  const [failed, setFailed] = useState(false);

  const send = useCallback(async () => {
    if (!slug) return;
    if (inFlightRef.current) {
      // Goes out when the running request settles — see the loop below.
      dirtyRef.current = true;
      return;
    }
    inFlightRef.current = true;
    setInFlight(true);
    try {
      for (;;) {
        dirtyRef.current = false;
        const sent = { ...pendingRef.current };
        const forced = forceRef.current;
        forceRef.current = false;
        if (Object.keys(sent).length === 0 && !forced) break;
        try {
          const next = await mutateAsync(sent);
          if (!dirtyRef.current) {
            qc.setQueryData(queryKeys.quote(slug), next);
            // Confirmed: drop exactly what was sent, keep anything newer.
            const rest = { ...pendingRef.current };
            for (const [key, value] of Object.entries(sent)) {
              if (rest[key] === value) delete rest[key];
            }
            pendingRef.current = rest;
            setPending(rest);
          }
          setFailed(false);
        } catch {
          if (!dirtyRef.current) {
            // Back to what the server holds — a pick it refused must not
            // stay on screen as if it had been priced.
            pendingRef.current = {};
            setPending({});
            setFailed(true);
          }
        }
        if (!dirtyRef.current) break;
      }
    } finally {
      inFlightRef.current = false;
      setInFlight(false);
    }
  }, [slug, mutateAsync, qc]);

  const refresh = useCallback(() => {
    forceRef.current = true;
    return send();
  }, [send]);

  const onChange = useCallback(
    (key: string, value: unknown) => {
      pendingRef.current = { ...pendingRef.current, [key]: value };
      setPending(pendingRef.current);
      setFailed(false);
      if (timerRef.current !== null) window.clearTimeout(timerRef.current);
      setWaiting(true);
      timerRef.current = window.setTimeout(() => {
        timerRef.current = null;
        setWaiting(false);
        void send();
      }, debounceMs);
    },
    [send, debounceMs],
  );

  useEffect(
    () => () => {
      if (timerRef.current !== null) window.clearTimeout(timerRef.current);
    },
    [],
  );

  const operations = quote?.operations;
  const serverInputs = useMemo(() => primaryInteractiveInputs(operations), [operations]);
  const inputs = useMemo(
    () =>
      serverInputs.map((input) =>
        input.key in pending
          ? {
              ...input,
              value: pending[input.key] === null ? input.default : pending[input.key],
            }
          : input,
      ),
    [serverInputs, pending],
  );

  return {
    binding: inputs.length > 0 ? { inputs, onChange, failed } : undefined,
    reanalyzing: waiting || inFlight,
    failed,
    refresh,
  };
}
